import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import type { SellerProductFiltersInput } from '~/graphql/graphql';

export const useGetMyProducts = (
  page: number,
  size: number,
  filters?: SellerProductFiltersInput
) => {
  const productsQuery = graphql(`
    query sellerProductInventories(
      $page: Int!
      $size: Int!
      $filters: SellerProductFiltersInput
    ) {
      sellerProductInventories(
        pageInput: { page: $page, size: $size }
        filters: $filters
      ) {
        paging {
          totalPages
          totalElements
          size
          number
        }
        content {
          id
          quantity {
            value
            unit
          }
          sellerProductConfiguration {
            id
            criticalStockLevel {
              value
              unit
            }
            currencyCode
            internalNotes
            lowStockLevel {
              value
              unit
            }
            readyToShipDays
            replenishmentRatePerWeek {
              value
              unit
            }
            pricePerUnit
            minimumOrderQuantity {
              value
              unit
            }
            status
            unit
          }
          platformProductsLength {
            id
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
            platformProduct {
              id
              name
              code
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    productsQuery,
    [
      'my-products',
      page.toString(),
      size.toString(),
      JSON.stringify(filters || null),
    ],
    { page, size, filters }
  );
};

export const useGetProductLengthsByProductId = (inventoryId: string) => {
  const productLengthsQuery = graphql(`
    query GetProductLengthsByProductId($inventoryId: ID!) {
      sellerProductInventory(id: $inventoryId) {
        id
        quantity {
          value
          unit
        }
        platformProductsLength {
          id
          length {
            id
            displayValue
            actualLengthInches
            nominalLengthInches
          }
          latestPriceMid
          platformProduct {
            id
            name
            materialCode
            constructionCode
            code
            properties {
              construction {
                code
                name
              }
              dryness {
                code
                name
              }
              grade {
                code
                name
              }
              species {
                code
                name
              }
              surfacing {
                code
                name
              }
              treatment {
                code
                name
              }
            }
          }
        }
        sellerProductConfiguration {
          id
          currencyCode
          internalNotes
          pricePerUnit
          replenishmentRatePerWeek {
            value
            unit
          }
          readyToShipDays
          criticalStockLevel {
            value
            unit
          }
          lowStockLevel {
            value
            unit
          }
          minimumOrderQuantity {
            value
            unit
          }
          status
          unit
        }
      }
    }
  `);

  return useGraphqlQuery(productLengthsQuery, ['my-products', inventoryId], {
    inventoryId,
  });
};

export const useUpsertSellerInventory = () => {
  const upsertInventoryMutation = graphql(`
    mutation UpsertSellerInventory(
      $platformProductsLengthId: ID!
      $quantity: QuantityInput!
    ) {
      upsertSellerInventory(
        platformProductsLengthId: $platformProductsLengthId
        quantity: $quantity
      ) {
        id
        quantity {
          value
          unit
        }
      }
    }
  `);

  return useGraphqlMutation(upsertInventoryMutation, ['my-products']);
};

export const useUpsertSellerProductConfiguration = () => {
  const upsertProductConfigMutation = graphql(`
    mutation UpsertSellerProductConfiguration(
      $platformProductLengthId: ID!
      $sellerProductConfigurationInput: SellerProductConfigurationInput!
    ) {
      upsertSellerProductConfiguration(
        platformProductLengthId: $platformProductLengthId
        sellerProductConfigurationInput: $sellerProductConfigurationInput
      ) {
        id
        criticalStockLevel {
          value
          unit
        }
        currencyCode
        internalNotes
        lowStockLevel {
          value
          unit
        }
        minimumOrderQuantity {
          value
          unit
        }
        pricePerUnit
        readyToShipDays
        replenishmentRatePerWeek {
          value
          unit
        }
        status
      }
    }
  `);

  return useGraphqlMutation(upsertProductConfigMutation, ['my-products']);
};

export const useUpsertSellerInventoryAndProductConfiguration = () => {
  const upsertInventoryAndConfigMutation = graphql(`
    mutation UpsertSellerInventoryAndProductConfiguration(
      $platformProductsLengthId: ID!
      $quantity: QuantityInput!
      $sellerProductConfigurationInput: SellerProductConfigurationInput!
    ) {
      upsertSellerInventory(
        platformProductsLengthId: $platformProductsLengthId
        quantity: $quantity
      ) {
        id
        quantity {
          value
          unit
        }
      }
      upsertSellerProductConfiguration(
        platformProductLengthId: $platformProductsLengthId
        sellerProductConfigurationInput: $sellerProductConfigurationInput
      ) {
        id
        criticalStockLevel {
          value
          unit
        }
        currencyCode
        internalNotes
        lowStockLevel {
          value
          unit
        }
        minimumOrderQuantity {
          value
          unit
        }
        pricePerUnit
        readyToShipDays
        replenishmentRatePerWeek {
          value
          unit
        }
        status
      }
    }
  `);

  return useGraphqlMutation(upsertInventoryAndConfigMutation, ['my-products']);
};

export const useGetMyProductFilterOptions = () => {
  const query = graphql(`
    query MyProductFilterOptions {
      lumberSpecies {
        code
        name
      }
      lumberGrades {
        code
        name
      }
      platformProductDimensions {
        nominalThicknessInches
        nominalWidthInches
      }
    }
  `);

  return useGraphqlQuery(query, ['myProductFilterOptions']);
};
